import React, { useMemo, useState } from "react";

interface Pattern {
  id: number;
  company_name: string;
  company_symbol: string;
  sector_name: string;
  timestamp: string;
  timeframe: string;
  pattern_type: string;
  confidence: number;
  volume_confirmed: boolean;
  signal: string;
  current_price: number | null;
}

interface RecentPatternsProps {
  patterns?: Pattern[];
}

const RecentPatterns: React.FC<RecentPatternsProps> = ({ patterns = [] }) => {
  const [filterSignal, setFilterSignal] = useState("all");
  const [filterTimeframe, setFilterTimeframe] = useState("all");

  const getSignalColor = (signal: string) => {
    switch (signal?.toLowerCase()) {
      case "bullish":
        return "is-success";
      case "bearish":
        return "is-danger";
      default:
        return "is-warning";
    }
  };

  const formatPatternName = (pattern: string) =>
    pattern
      ?.split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const filteredPatterns = useMemo(() => {
    if (!Array.isArray(patterns)) return [];

    return patterns.filter((pattern) => {
      const signalMatch =
        filterSignal === "all" || pattern.signal === filterSignal;

      const timeframeMatch =
        filterTimeframe === "all" || pattern.timeframe === filterTimeframe;

      return signalMatch && timeframeMatch;
    });
  }, [patterns, filterSignal, filterTimeframe]);

  return (
    <div className="box recent-patterns">
      <div className="level mb-4">
        <div className="level-left">
          <h2 className="title is-5">
            <span className="icon">
              <i className="fa fa-chart-line" />
            </span>
            <span> Recent Patterns</span>
          </h2>
        </div>

        <div className="level-right">
          <div className="level-item">
            <div className="select is-small">
              <select
                value={filterSignal}
                onChange={(e) => setFilterSignal(e.target.value)}
              >
                <option value="all">All Signals</option>
                <option value="bullish">Bullish</option>
                <option value="bearish">Bearish</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>
          </div>

          <div className="level-item">
            <div className="select is-small">
              <select
                value={filterTimeframe}
                onChange={(e) => setFilterTimeframe(e.target.value)}
              >
                <option value="all">All Timeframes</option>
                <option value="5min">5min</option>
                <option value="15min">15min</option>
                <option value="1hour">1hour</option>
                <option value="1day">1day</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {filteredPatterns.length === 0 ? (
        <div className="notification is-light has-text-centered">
          No patterns found
        </div>
      ) : (
        <div className="table-container">
          <table className="table is-fullwidth is-hoverable is-striped">
            <thead>
              <tr>
                <th>Company</th>
                <th>Pattern</th>
                <th>Timeframe</th>
                <th>Signal</th>
                <th>Confidence</th>
                <th>Volume</th>
                <th>Price</th>
                <th>Time</th>
              </tr>
            </thead>

            <tbody>
              {filteredPatterns.map((pattern) => (
                <tr key={pattern.id}>
                  <td>
                    <p className="has-text-weight-bold">
                      {pattern.company_symbol}
                    </p>
                    <p className="is-size-7 has-text-grey">
                      {pattern.sector_name}
                    </p>
                  </td>

                  <td>
                    <span className="tag is-light">
                      {formatPatternName(pattern.pattern_type)}
                    </span>
                  </td>

                  <td>
                    <span className="tag is-info is-light">
                      {pattern.timeframe}
                    </span>
                  </td>

                  <td>
                    <span className={`tag ${getSignalColor(pattern.signal)}`}>
                      {pattern.signal}
                    </span>
                  </td>

                  <td>
                    <div className="tags has-addons">
                      <span className="tag is-dark">
                        {(pattern.confidence * 100).toFixed(0)}%
                      </span>
                      <span
                        className={`tag ${
                          pattern.confidence >= 0.8
                            ? "is-success"
                            : pattern.confidence >= 0.6
                            ? "is-warning"
                            : "is-danger"
                        }`}
                      >
                        {pattern.confidence >= 0.8
                          ? "High"
                          : pattern.confidence >= 0.6
                          ? "Med"
                          : "Low"}
                      </span>
                    </div>
                  </td>

                  <td>
                    {pattern.volume_confirmed ? (
                      <span className="tag is-success">
                        <i className="fa fa-check" />
                      </span>
                    ) : (
                      <span className="tag is-light">
                        <i className="fa fa-times" />
                      </span>
                    )}
                  </td>

                  <td className="has-text-weight-bold">
                    ₹{pattern.current_price?.toFixed(2) ?? "N/A"}
                  </td>

                  <td className="is-size-7">
                    {new Date(pattern.timestamp).toLocaleTimeString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RecentPatterns;
